import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { imgUrl } from "@/lib/api";

const POS = { centre: "center", haut: "top", bas: "bottom", gauche: "left", droite: "right" };
const posOf = (v) => POS[v] || "center";

/**
 * Section Avant/Après à scroll-lock total :
 * - IntersectionObserver détecte l'entrée dans le viewport -> la page se fige
 *   (body overflow hidden + position de scroll verrouillée + Lenis stoppé).
 * - Les événements wheel / touchmove sont interceptés et font uniquement
 *   progresser le clip-path de l'image "après" (de 100% à 0%).
 * - À 0% (révélation complète, scroll vers le bas) ou 100% (retour vers le
 *   haut), le scroll normal est relâché. Rien d'autre ne bouge pendant.
 */
export default function ScrubSection({ settings }) {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const afterRef = useRef(null);
  const lineRef = useRef(null);
  const stateRef = useRef({ locked: false, progress: 0, touchY: 0, lastOffset: null, lockedY: 0 });

  const before = settings.hero_photo_before || settings.hero_photo_main;
  const after = settings.hero_photo_after || before;
  const active = !reduce && before && after && before !== after;
  const pos = posOf(settings.hero_photo_main_position);

  useEffect(() => {
    if (!active) return undefined;
    const section = sectionRef.current;
    if (!section) return undefined;
    const st = stateRef.current;

    const render = () => {
      const v = 100 - st.progress * 100;
      if (afterRef.current) afterRef.current.style.clipPath = `inset(0 ${v}% 0 0)`;
      if (lineRef.current) {
        lineRef.current.style.left = `${st.progress * 100}%`;
        lineRef.current.style.opacity = st.progress > 0.02 && st.progress < 0.98 ? "1" : "0";
      }
    };

    const lock = () => {
      st.locked = true;
      st.lockedY = window.scrollY;
      // Pas de snap : la page se fige exactement là où elle est (évite le "téléport").
      if (window.__lenis) {
        window.__lenis.velocity = 0;
        window.__lenis.stop();
      }
      document.body.style.overflow = "hidden";
    };

    const unlock = () => {
      st.locked = false;
      st.touchY = 0;
      document.body.style.overflow = "";
      if (window.__lenis) {
        window.__lenis.velocity = 0;
        window.__lenis.start();
      }
    };

    const advance = (delta) => {
      const next = Math.min(1, Math.max(0, st.progress + delta));
      st.progress = next;
      render();
      // Déverrouille dès qu'on franchit une borne : 100% en descendant, 0% en remontant
      if (delta > 0 && next >= 1) unlock();
      else if (delta < 0 && next <= 0) unlock();
    };

    const onWheel = (e) => {
      // L'utilisateur reprend la main : fin du mode "saut" éventuel.
      window.__jumpNav = false;
      if (!st.locked) return;
      e.preventDefault();
      advance(e.deltaY * 0.0012);
    };
    const onTouchStart = (e) => {
      window.__jumpNav = false;
      st.touchY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (!st.locked) return;
      e.preventDefault();
      const y = e.touches[0].clientY;
      advance((st.touchY - y) * 0.002);
      st.touchY = y;
    };
    const onKeyDown = (e) => {
      if (!st.locked) return;
      if ([" ", "ArrowDown", "ArrowUp", "PageDown", "PageUp"].includes(e.key)) {
        e.preventDefault();
        advance(e.key === "ArrowUp" || e.key === "PageUp" ? -0.08 : 0.08);
      }
    };

    // Détection pilotée par le scroll réel (chaque frame Lenis ou native) :
    // on verrouille quand le CENTRE de la section TRAVERSE le centre du
    // viewport — un franchissement de signe ne peut pas être raté, même avec
    // l'inertie tactile mobile qui fait sauter la position de +100px par frame.
    const onScrollCheck = () => {
      if (st.locked) {
        if (window.__jumpNav) {
          // Clic de navigation pendant le verrouillage : on libère le scroll,
          // la synchro ci-dessous (drapeau actif) mettra le slider à jour.
          unlock();
        } else {
          // Sécurité inertie tactile : si le scroll continue malgré le verrou, on rétablit la position
          if (Math.abs(window.scrollY - st.lockedY) > 1) window.scrollTo(0, st.lockedY);
          return;
        }
      }
      const r = section.getBoundingClientRect();
      const off = r.top + r.height / 2 - window.innerHeight / 2;
      const prev = st.lastOffset;
      st.lastOffset = off;
      if (window.__jumpNav) {
        // Saut via bouton/ancre : JAMAIS de verrouillage. Si la destination
        // est en dessous de la section, le slider se révèle tout seul
        // (comme si on avait scrollé à travers) ; au-dessus, état inchangé.
        const destY = window.__jumpNavY;
        const sectionBottom = window.scrollY + r.top + r.height;
        if (destY != null && destY >= sectionBottom && st.progress !== 1) {
          st.progress = 1;
          render();
        }
        return;
      }
      if (prev == null) return;
      const crossingDown = prev > 0 && off <= 0; // scroll vers le bas
      const crossingUp = prev < 0 && off >= 0; // scroll vers le haut
      if (!crossingDown && !crossingUp) return;
      // Révélation en cours -> on bloque, quelle que soit la direction
      if (st.progress > 0 && st.progress < 1) {
        lock();
        return;
      }
      if (crossingDown && st.progress >= 1) return; // déjà révélé : passage libre vers le bas
      if (crossingUp && st.progress <= 0) return; // déjà refermé : passage libre vers le haut
      lock();
    };

    // Permet à scrollToId() de libérer le scroll si un clic de navigation
    // survient pendant le verrouillage (sinon le saut serait combattu).
    window.__scrubRelease = () => { if (st.locked) unlock(); };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScrollCheck, { passive: true });

    render();
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScrollCheck);
      window.__scrubRelease = null;
      if (st.locked) unlock();
    };
  }, [active]);

  if (!before) return null;

  return (
    <section
      ref={sectionRef}
      data-testid="before-after-section"
      className="relative p-0 m-0 w-full h-[60svh] md:h-[80svh] flex items-center justify-center overflow-hidden"
    >
      <div className="relative w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="relative">
          <div className="absolute -top-3 -right-3 bottom-3 left-3 rounded-[1.75rem] border border-line hidden sm:block" aria-hidden="true" />
          <div
            className="relative h-[300px] md:h-[500px] w-full overflow-hidden rounded-[1.25rem] md:rounded-[1.75rem] shadow-[0_24px_60px_-24px_rgba(16,24,15,0.28)]"
            data-testid="hero-scrub-photo"
          >
            <img
              src={imgUrl(before)}
              alt={settings.hero_photo_before_alt || "Intervention MARY SERVICES — avant"}
              fetchPriority="high"
              style={{ objectPosition: pos }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            {active && (
              <>
                <div
                  ref={afterRef}
                  className="absolute inset-0"
                  style={{ clipPath: "inset(0 100% 0 0)" }}
                  data-testid="hero-after-layer"
                >
                  <img
                    src={imgUrl(after)}
                    alt={settings.hero_photo_after_alt || "Intervention MARY SERVICES — après"}
                    style={{ objectPosition: pos }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <div
                  ref={lineRef}
                  className="absolute top-0 bottom-0 w-0.5 bg-white/90 shadow-[0_0_14px_rgba(0,0,0,0.45)]"
                  style={{ left: "0%", opacity: 0 }}
                  aria-hidden="true"
                >
                  <span className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-lg grid place-items-center ring-4 ring-white/20">
                    <ChevronsLeftRight className="h-[18px] w-[18px] text-forest" />
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
